<template>
  <div class="materials-management">
    <!-- 搜索和操作栏 -->
    <div class="toolbar">
      <div class="toolbar-left">
        <el-input
          v-model="searchForm.title"
          placeholder="搜索资料标题、分类"
          style="width: 300px"
          @keyup.enter="handleSearch"
        >
          <el-button slot="append" icon="el-icon-search" @click="handleSearch"></el-button>
        </el-input>

        <el-cascader
          v-model="searchForm.category"
          :options="trainingCategoryTree"
          :props="categoryFilterProps"
          placeholder="选择分类"
          clearable
          filterable
          style="width: 220px; margin-left: 10px"
          @change="handleSearch"
        />

        <el-select
          v-model="searchForm.materialType"
          placeholder="资料类型"
          clearable
          style="width: 120px; margin-left: 10px"
          @change="handleSearch"
        >
          <el-option
            v-for="materialType in materialTypeOptions"
            :key="materialType.value"
            :label="materialType.label"
            :value="materialType.value"
          />
        </el-select>
      </div>

      <div class="toolbar-right">
        <el-button type="primary" @click="handleCreate">
          <i class="el-icon-plus"></i>
          新增资料
        </el-button>
        <el-button @click="handleSearch">
          <i class="el-icon-refresh"></i>
          刷新
        </el-button>
      </div>
    </div>

    <!-- 资料列表 -->
    <el-table
      :data="materialsList"
      stripe
      style="width: 100%"
      @selection-change="handleSelectionChange"
    >
      <el-table-column type="selection" width="55" />

      <el-table-column prop="title" label="资料标题" min-width="200">
        <template slot-scope="scope">
          <div class="title-cell">
            <span class="title-text">{{ scope.row.title }}</span>
          </div>
        </template>
      </el-table-column>

      <el-table-column prop="category" label="分类" min-width="160">
        <template slot-scope="scope">
          <el-tag size="small">{{ trainingCategoryText(scope.row.category) || "未分类" }}</el-tag>
        </template>
      </el-table-column>

      <el-table-column prop="description" label="描述" min-width="200" show-overflow-tooltip />

      <el-table-column prop="estimatedDuration" label="预计时长" width="100">
        <template slot-scope="scope">
          <span v-if="scope.row.estimatedDuration"> {{ scope.row.estimatedDuration }}分钟 </span>
          <span v-else class="text-muted">未设置</span>
        </template>
      </el-table-column>

      <el-table-column label="过期天数" width="100">
        <template slot-scope="scope">
          <span v-if="scope.row.expireDays != null && scope.row.expireDays >= 0">{{ scope.row.expireDays }}天</span>
          <span v-else class="text-muted">不限</span>
        </template>
      </el-table-column>

      <el-table-column prop="files" label="文件数量" width="100">
        <template slot-scope="scope">
          <el-tag size="mini" type="info">
            {{ scope.row.files ? scope.row.files.length : 0 }}个
          </el-tag>
        </template>
      </el-table-column>

      <el-table-column prop="enabled" label="状态" width="80">
        <template slot-scope="scope">
          <el-tag :type="scope.row.enabled ? 'success' : 'danger'" size="mini">
            {{ scope.row.enabled ? "启用" : "禁用" }}
          </el-tag>
        </template>
      </el-table-column>

      <el-table-column prop="createTime" label="创建时间" width="160">
        <template slot-scope="scope">
          {{ scope.row.createTime | dateFormat("yyyy-MM-dd hh:mm") }}
        </template>
      </el-table-column>

      <el-table-column label="操作" width="300" fixed="right">
        <template slot-scope="scope">
          <el-button size="mini" @click="handleView(scope.row)">查看</el-button>
          <el-button size="mini" type="primary" @click="handleEdit(scope.row)">编辑</el-button>
          <el-button size="mini" type="danger" @click="handleDelete(scope.row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <div class="pagination-container">
      <el-pagination
        @current-change="handleCurrentChange"
        background
        :current-page="pagination.currentPage"
        :page-size="pagination.pageSize"
        layout="total, prev, pager, next, jumper"
        :total="pagination.total"
      />
    </div>

    <!-- 新增/编辑对话框 -->
    <MaterialsDialog
      :visible.sync="dialogVisible"
      :form-data="currentFormData"
      :mode="dialogMode"
      @confirm="handleDialogConfirm"
    />

    <!-- 查看对话框 -->
    <MaterialsViewDialog :visible.sync="viewDialogVisible" :materials-data="viewData" />
  </div>
</template>

<script>
import MaterialsDialog from "./MaterialsDialog.vue";
import MaterialsViewDialog from "./MaterialsViewDialog.vue";
import { BaseDataService } from "@/service";
import { getTrainingMaterialsList, deleteTrainingMaterials } from "@/api/new_training";
import { ParamUtil } from "@/util";
import trainingCategoryMixin from "../utils/trainingCategory";
export default {
  name: "MaterialsManagement",
  mixins: [trainingCategoryMixin],
  components: {
    MaterialsDialog,
    MaterialsViewDialog
  },
  data() {
    return {
      materialsList: [],
      selectedRows: [],

      // 搜索表单
      searchForm: {
        keyword: "",
        category: "",
        materialType: ""
      },

      materialTypeOptions: BaseDataService.trainingMaterialTypeOpts(),
      // 分页
      pagination: {
        currentPage: 1,
        pageSize: 20,
        total: 0
      },

      // 对话框
      dialogVisible: false,
      dialogMode: "create", // create | edit
      currentFormData: {},

      // 查看对话框
      viewDialogVisible: false,
      viewData: {}
    };
  },
  created() {
    this.query();
  },
  methods: {
    // 加载资料列表（后台分页）
    query(page = 1) {
      const param = {
        page: {
          current: page,
          size: this.pagination.pageSize
        },
        param: ParamUtil.filterEmptyData(this.searchForm)
      };
      getTrainingMaterialsList(param)
        .then((res) => {
          const { records = [], current = 1, total = 0 } = res.data || {};
          this.materialsList = records;
          this.pagination.currentPage = current;
          this.pagination.total = total;
        })
        .catch((error) => {
          this.$message.error("加载资料列表失败");
          console.error(error);
        });
    },

    // 刷新/搜索
    handleSearch() {
      this.pagination.currentPage = 1;
      this.query(1);
    },

    // 新增
    handleCreate() {
      this.dialogMode = "create";
      this.currentFormData = {};
      this.dialogVisible = true;
    },

    // 编辑
    handleEdit(row) {
      this.dialogMode = "edit";
      this.currentFormData = { ...row };
      this.dialogVisible = true;
    },

    // 查看
    handleView(row) {
      this.viewData = { ...row };
      this.viewDialogVisible = true;
    },

    // 删除
    handleDelete(row) {
      this.$confirm("确定要删除这个培训资料吗？", "提示", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning"
      }).then(async () => {
        try {
          await deleteTrainingMaterials(row.id);
          this.$message.success("删除成功");
          this.query(this.pagination.currentPage); // 删除后刷新当前页
        } catch (error) {
          this.$message.error("删除失败");
        }
      });
    },

    // 选择变化
    handleSelectionChange(selection) {
      this.selectedRows = selection;
    },

    // 分页变化
    handleCurrentChange(page) {
      this.query(page);
    },

    // 对话框确认
    async handleDialogConfirm() {
      this.$message.success("保存成功");
      this.query(this.pagination.currentPage); // 编辑后刷新当前页
    },

    // 获取资料类型标签样式
    getMaterialTypeTag(item) {
      const type = item.files[0].fileType;
      const typeMap = {
        VIDEO: "primary",
        PDF: "success",
        IMAGE: "warning"
      };
      return typeMap[type] || "info";
    }
  }
};
</script>

<style lang="less" scoped>
.materials-management {
  .toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    padding: 16px;
    background: #f8f9fa;
    border-radius: 6px;

    .toolbar-left {
      display: flex;
      align-items: center;
    }

    .toolbar-right {
      .el-button {
        margin-left: 8px;
      }
    }
  }

  .title-cell {
    display: flex;
    align-items: center;

    .title-text {
      font-weight: 500;
    }
  }

  .pagination-container {
    margin-top: 20px;
    text-align: right;
  }

  .text-muted {
    color: #909399;
  }
}
</style>
